export interface SiteConfig {
  // Identity
  name: string;
  city: string;
  state: string;
  tagline: string;
  domain: string;

  // SEO
  seo: {
    defaultDescription: string;
    defaultImage?: string;
    twitterHandle?: string;
    pages: {
      homeTitle: string;
      homeDescription: string;
      eventsTitle: string;
      eventsDescription: string;
      businessesTitle: string;
      businessesDescription: string;
      guideTitle: string;
      guideDescription: string;
      pricingTitle: string;
      pricingDescription: string;
      aboutTitle: string;
      aboutDescription: string;
      resourcesTitle: string;
      resourcesDescription: string;
      termsTitle: string;
      privacyTitle: string;
      contactTitle: string;
      contactDescription: string;
      loginTitle: string;
      signupTitle: string;
      forgotPasswordTitle: string;
      resetPasswordTitle: string;
      authCallbackTitle: string;
      dashboardTitle: string;
      adminTitle: string;
      profileTitle: string;
      eventSubmissionTitle: string;
      eventCalendarTitle: string;
      businessCreationTitle: string;
      paymentTitle: string;
      paymentSuccessTitle: string;
      paymentCancelTitle: string;
    };
  };

  // Theme
  seedColor: string;

  // Features
  features: {
    chat: boolean;
    weather: boolean;
    events: boolean;
    businesses: boolean;
    guide: boolean;
    jobs: boolean;
  };

  // Chat
  chatBotName: string;
  chatSystemPrompt: string;
  chatWorkerUrl: string;

  // Weather
  weather: {
    lat: number;
    lon: number;
    name: string;
  };

  // Stripe price IDs
  stripe: {
    freePriceId: string | null;
    basicPriceId: string;
    premiumPriceId: string;
    spotlightPriceId: string;
  };

  // Guide content
  guide: {
    gettingHere: string;
    bestTimeToVisit: string;
    attractions: string;
    attractionsList: { name: string; description: string; image: string }[];
    importantLocations: { name: string; detail: string }[];
    annualEvents: { name: string; month: string }[];
  };

  // Legal
  legal: {
    arbitrationLocation: string;
    address: string;
    zipCode: string;
  };

  // Contact
  contact: {
    phone?: string;
    email?: string;
  };

  // Social links
  social: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };

  // Default business city/state for forms
  defaults: {
    city: string;
    state: string;
  };

}

export const siteConfig: SiteConfig = {
  name: 'Palmdale.info',
  city: 'Palmdale',
  state: 'CA',
  tagline: 'The Aerospace Capital of the Antelope Valley — where innovation meets community',
  domain: 'palmdale.info',

  seo: {
    defaultDescription: 'Discover Palmdale, California — local businesses, events, visitor guide, and community resources in the aerospace capital of the Antelope Valley.',
    defaultImage: 'https://ohgpinzrvrdgyoljnzkf.supabase.co/storage/v1/object/public/site-images/hero/aerospace-hero.png',
    twitterHandle: '',
    pages: {
      homeTitle: 'Home',
      homeDescription: 'Discover Palmdale, California — local businesses, events, visitor guide, and community resources in the aerospace capital of the Antelope Valley.',
      eventsTitle: 'Events',
      eventsDescription: 'Find upcoming events, concerts at the Palmdale Amphitheater, festivals, and community gatherings in Palmdale, CA.',
      businessesTitle: 'Local Businesses',
      businessesDescription: 'Explore local businesses, restaurants, aerospace contractors, and services in Palmdale, CA.',
      guideTitle: 'Visitor Guide',
      guideDescription: 'Plan your visit to Palmdale, CA — Blackbird Airpark, DryTown Water Park, Plant 42, and the best of the Antelope Valley.',
      pricingTitle: 'Pricing',
      pricingDescription: 'Compare listing plans and promotion options for Palmdale businesses.',
      aboutTitle: 'About',
      aboutDescription: 'Learn how Palmdale.info connects the community with local businesses, events, and resources.',
      resourcesTitle: 'Community Resources',
      resourcesDescription: 'Essential city services, department contacts, parks, and community resources for Palmdale residents and visitors.',
      termsTitle: 'Terms of Service',
      privacyTitle: 'Privacy Policy',
      contactTitle: 'Contact',
      contactDescription: 'Send a message to the Palmdale.info team.',
      loginTitle: 'Log In',
      signupTitle: 'Sign Up',
      forgotPasswordTitle: 'Forgot Password',
      resetPasswordTitle: 'Reset Password',
      authCallbackTitle: 'Authentication',
      dashboardTitle: 'Dashboard',
      adminTitle: 'Admin',
      profileTitle: 'Profile',
      eventSubmissionTitle: 'Submit Event',
      eventCalendarTitle: 'Event Calendar',
      businessCreationTitle: 'Create Business Profile',
      paymentTitle: 'Payment',
      paymentSuccessTitle: 'Payment Success',
      paymentCancelTitle: 'Payment Cancelled',
    },
  },

  seedColor: '#1E3A8A',

  features: {
    chat: false,
    weather: true,
    events: true,
    businesses: true,
    guide: true,
    jobs: true,
  },

  chatBotName: 'PalmdaleBot',
  chatSystemPrompt: 'You are a helpful assistant for Palmdale, CA. Help visitors learn about local businesses, events, aerospace heritage, and attractions in the Antelope Valley.',
  chatWorkerUrl: '',

  weather: { lat: 34.5794, lon: -118.1165, name: 'Palmdale' },

  stripe: {
    freePriceId: null,
    basicPriceId: import.meta.env.VITE_STRIPE_BASIC_PRICE_ID || 'price_1TGJAn2L6siz0VxCwA72QQIL',
    premiumPriceId: import.meta.env.VITE_STRIPE_PREMIUM_PRICE_ID || 'price_1TGJBh2L6siz0VxCqdLNAU35',
    spotlightPriceId: import.meta.env.VITE_STRIPE_SPOTLIGHT_PRICE_ID || 'price_1TGJDD2L6siz0VxCdIby6loL',
  },

  guide: {
    gettingHere: 'Palmdale is located in the western Mojave Desert along the CA-14 (Antelope Valley Freeway) in Los Angeles County. It\'s approximately 60 miles north of Downtown Los Angeles (1–1.5 hours depending on traffic). The nearest commercial airport is Hollywood Burbank Airport (BUR, ~50 miles south) or Los Angeles International Airport (LAX, ~70 miles south). Palmdale Regional Airport (PMD) is local but has limited commercial service.',
    bestTimeToVisit: 'Spring (March–May) is spectacular with the famous California Poppy blooms at the nearby Antelope Valley Poppy Reserve. Fall (October–November) offers pleasant temperatures in the 70s. Summers are very hot — often exceeding 100°F — so bring sun protection and plenty of water. Winters are cool and breezy, with occasional frost and stunning clear skies.',
    attractions: 'Palmdale is defined by aerospace — it\'s home to U.S. Air Force Plant 42, where Lockheed Martin\'s Skunk Works and Northrop Grumman build the future of flight. Visitors can see legendary aircraft like the SR-71 Blackbird up close at the Blackbird Airpark. Beyond aviation, the city offers world-class outdoor recreation, summer fun at DryTown Water Park, concerts at the 10,000-seat Palmdale Amphitheater, and over 20 city parks.',
    attractionsList: [
      {
        name: 'Blackbird Airpark',
        description: 'Aviation museum featuring the legendary SR-71 Blackbird and other spy planes',
        image: 'https://ohgpinzrvrdgyoljnzkf.supabase.co/storage/v1/object/public/site-images/guide/blackbird-airpark.png',
      },
      {
        name: 'Joe Davies Heritage Airpark',
        description: 'Outdoor museum with military aircraft built and tested at neighboring Plant 42',
        image: 'https://ohgpinzrvrdgyoljnzkf.supabase.co/storage/v1/object/public/site-images/guide/joe-davies-airpark.png',
      },
      {
        name: 'Palmdale Amphitheater',
        description: 'Premier 10,000-capacity outdoor venue hosting summer concerts and community festivals',
        image: 'https://ohgpinzrvrdgyoljnzkf.supabase.co/storage/v1/object/public/site-images/guide/palmdale-amphitheater.png',
      },
      {
        name: 'DryTown Water Park',
        description: 'Family aquatic park with water slides, lazy river, and splash pads — perfect for hot desert summers',
        image: 'https://ohgpinzrvrdgyoljnzkf.supabase.co/storage/v1/object/public/site-images/guide/drytown-water-park.png',
      },
      {
        name: 'Palmdale Oasis Park',
        description: '33-acre recreation complex with sports fields, pool, recreation center, and DryTown',
        image: 'https://ohgpinzrvrdgyoljnzkf.supabase.co/storage/v1/object/public/site-images/guide/palmdale-oasis-park.png',
      },
      {
        name: 'Marie Kerr Park & Amphitheater',
        description: 'Community hub with recreation center, pool, sports courts, and outdoor amphitheater',
        image: 'https://ohgpinzrvrdgyoljnzkf.supabase.co/storage/v1/object/public/site-images/guide/marie-kerr-park.png',
      },
    ],
    importantLocations: [
      { name: 'City Hall', detail: '38300 Sierra Hwy — (661) 267-5100' },
      { name: 'Palmdale Sheriff\'s Station (LASD)', detail: '750 E Avenue Q — (661) 272-2400' },
      { name: 'LA County Fire Station 37', detail: '38318 9th St E — (661) 947-7244' },
      { name: 'Post Office', detail: '2270 E Palmdale Blvd — (800) 275-8777' },
      { name: 'Palmdale City Library', detail: '700 E Palmdale Blvd — (661) 267-5600' },
      { name: 'Palmdale Regional Medical Center', detail: '38600 Medical Center Dr — (661) 382-5000' },
      { name: 'Legacy Commons for Active Seniors', detail: '930 E Avenue Q-9 — (661) 267-5904' },
      { name: 'Chimbole Cultural Center', detail: '38350 Sierra Hwy — (661) 267-5656' },
    ],
    annualEvents: [
      { name: 'Amphitheater Summer Concert Series', month: 'July–August' },
      { name: 'Kaleidoscope Art & Music Festival', month: 'October' },
      { name: 'DryTown Water Park Opening', month: 'May' },
      { name: 'Thursday Night on the Square', month: 'June–August' },
      { name: 'Holiday Parade', month: 'December' },
      { name: 'Senior Resource Fair', month: 'April' },
    ],
  },

  legal: {
    arbitrationLocation: 'Los Angeles County, California',
    address: '38300 Sierra Highway',
    zipCode: '93550',
  },

  contact: {
    phone: '(661) 267-5100',
    email: 'info@palmdale.info',
  },

  social: {},

  defaults: {
    city: 'Palmdale',
    state: 'CA',
  },
};
